import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Text } from '@hubdevs/viu-ui-shared';
import { FormGroup, FormLabel, Radio } from "@mui/material";
import { QuoteExperienceState } from "../QuoteExperienceQuickSetup.page";
import { AppFormRow } from "../../../components/AppFormRow/AppFormRow";
import { AppFormCell } from "../../../components/AppFormCell/AppFormCell";
import { AppTextField } from "../../../components/AppTextField/AppTextField.component";
import { AppTableWithButtons } from "../../../components/TableWithButtons/TableWithButtons";
import { ApiServiceContext } from "../../../api/ApiService.context";
import { OrganizationService } from "../../../api/organization/OrganizationService.class";
import { AttributionService } from "../../../api/attribution/AttributionService.class";
import { BundleService } from "../../../api/bundle/BundleService.class";

type PartnerAttribution = {
    attributionGuid: string | null;
    displayName: string;
    status: string;
    type: string;
}

const UNSELECTED_ID = 'N/A';
type DataLoading = 'NOT STARTED' | 'LOADING' | 'DONE';

export const QuoteExperienceBase: FC<{
    organizationGuid: string;
    bundleGuid?: string;
    initial: Partial<QuoteExperienceState>;
    setIsLoading: (key: string, value: boolean) => void;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    setCanSaveBySection: (section: string, canSave: string | null) => void;
}> = ({ organizationGuid, bundleGuid, initial, setIsLoading, updateData, setCanSaveBySection }) => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { 
        OrganizationService, 
        AttributionService, 
        BundleService
    } = getApiServiceCollections() as {
        OrganizationService: OrganizationService;
        AttributionService: AttributionService;
        BundleService: BundleService;
    };
    const [displayName, setDisplayName] = useState<string>(initial.displayName || '');
    const [partnerAttribution, setPartnerAttribution] = useState<string | undefined>(initial.partnerAttribution);
    // Sync partner attribution when loaded from existing bundle
    useEffect(() => {
        if (initial.partnerAttribution !== undefined) {
            setPartnerAttribution(initial.partnerAttribution);
        }
    }, [initial.partnerAttribution]);
    const [error, setError] = useState<string>('');
    const [bundleLoadState, setBundleLoadState] = useState<DataLoading>('NOT STARTED');
    const [orgAttrsLoadState, setOrgAttrsLoadState] = useState<DataLoading>('NOT STARTED');
    const [attrsLoading, setAttrsLoading] = useState<boolean>(false);

    const [orgAttrGuids, setOrgAttrGuids] = useState<Set<string>>(new Set());
    const [partnerAttributions, setPartnerAttributions] = useState<PartnerAttribution[]>([]);

    useEffect(() => {
        // Load bundle
        if (!bundleGuid || bundleLoadState !== 'NOT STARTED') {
            return
        }
        const fetchBundle = async () => {
            setBundleLoadState('LOADING');
            setIsLoading('QuoteExperienceBase: Bundle', true);
            let getBundleResp = await BundleService.getBundleById(bundleGuid);
            while (getBundleResp.retry) {
                getBundleResp = await getBundleResp.retry.catch(error => error);
            }
            if (getBundleResp.error) {
                setIsLoading('QuoteExperienceBase: Bundle', false);
                setBundleLoadState('DONE');
                return;
            }
            setDisplayName(getBundleResp.jsonBody!.displayName);
            updateData('/displayName', getBundleResp.jsonBody!.displayName, true);
            setIsLoading('QuoteExperienceBase: Bundle', false);
        }
        fetchBundle();
    }, [BundleService, bundleGuid, bundleLoadState, setIsLoading, updateData]);

    useEffect(() => {
        if (orgAttrsLoadState !== 'NOT STARTED') {
            return;
        }
        const fetchOrgAttrs = async () => {
            setOrgAttrsLoadState('LOADING');
            setError('');
            setIsLoading('QuoteExperienceBase: OrgAttr', true);
            let getOrgAttrsResp = await OrganizationService.getOrganizationAttributions({ organizationGuid, skip: 0, limit: 1000 });
            while (getOrgAttrsResp.retry) {
                getOrgAttrsResp = await getOrgAttrsResp.retry.catch(error => error);
            }
            if (getOrgAttrsResp.error) {
                setIsLoading('QuoteExperienceBase: OrgAttr', false);
                setOrgAttrsLoadState('DONE');
                return;
            }
            const rows = getOrgAttrsResp.jsonBody!.data ?? [];
            const guids = new Set<string>(rows.map((r: any) => r.attributionGuid));
            setOrgAttrGuids(guids);
            setIsLoading('QuoteExperienceBase: OrgAttr', false);
        }
        fetchOrgAttrs();
    }, [orgAttrsLoadState, organizationGuid, OrganizationService, setIsLoading]);

    const loadPartnerAttributions = useCallback(async (page: number, pageSize: number) => {
        setError('');
        setIsLoading('QuoteExperienceBase: Attr', true);
        setAttrsLoading(true);

        let attrsResp = await AttributionService.getAttributions(
            { skip: page * pageSize, limit: pageSize, query: {
                filter: {
                    type: 'PARTNER'
                }
            }}
        );
        while (attrsResp.retry) {
            attrsResp = await attrsResp.retry.catch(error => error);
        }
        if (attrsResp.error) {
            setIsLoading('QuoteExperienceBase: Attr', false);
            setAttrsLoading(false);
            return;
        }
        const rows = attrsResp.jsonBody!.data ?? [];
        setPartnerAttributions(rows);
        setAttrsLoading(false);
    }, [AttributionService, setIsLoading]);

    const permittedPartnerAttributions = useMemo(() => {
        if (!orgAttrGuids.size || attrsLoading) return [];
        const ppa = partnerAttributions.filter((p: any) => orgAttrGuids.has(p.attributionGuid));
        ppa.unshift({
            attributionGuid: UNSELECTED_ID,
            displayName: 'Inherit from Organization',
            status: 'ACTIVE',
            type: 'PARTNER'
        });
        return ppa;
    }, [partnerAttributions, attrsLoading, orgAttrGuids]);

    const handlePartnerAttributionChange = (guid: string) => {
        const newVal = guid === UNSELECTED_ID ? undefined : guid;
        setPartnerAttribution(newVal);
        updateData('/partnerAttribution', newVal)
    }

    const handleDisplayNameChange = (value: string) => {
        setDisplayName(value);
        updateData('/displayName', value);
    }

    useEffect(() => {
        setCanSaveBySection('base', displayName ? null : 'A display name must be provided.');
    }, [displayName, setCanSaveBySection]);

    const partnerColumns = [
        {
            field: 'select',
            headerName: '',
            width: 64,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => {
                const guid = params.row.attributionGuid;
                const checked = (!partnerAttribution && guid === UNSELECTED_ID) || partnerAttribution === guid;
                return (
                    <Radio
                        checked={checked}
                        onChange={() => handlePartnerAttributionChange(guid)}
                        inputProps={{ 'aria-label': `Select partner ${guid}` }}
                    />
                );
            },
        },
        { field: 'displayName', headerName: 'Name', flex: 1, width: 300 },
        { field: 'attributionGuid', headerName: 'GUID', flex: 1, width: 300 },
        { field: 'status', headerName: 'Status', width: 120 },
        { field: 'type', headerName: 'Type', width: 140 },
    ];

    const handlePageChange = useCallback((page: number, pageSize: number) => {
        if (orgAttrGuids.size) {
            loadPartnerAttributions(page, pageSize);
        }
    }, [orgAttrGuids.size, loadPartnerAttributions]);

    return (
        <>
            <AppFormRow>
                <AppFormCell>
                    <FormLabel htmlFor="displayName">Display Name</FormLabel>
                    <FormGroup row>
                        <AppTextField
                            name="displayName"
                            required
                            disabled={!!initial.id}
                            value={displayName}
                            onChange={(e) => handleDisplayNameChange(e.target.value)}
                        />
                    </FormGroup>
                </AppFormCell>
            </AppFormRow>

            <AppFormRow>
                <AppFormCell style={{ width: '100%' }}>
                    {error ? (
                        <Text>{error}</Text>
                    ) : (
                        <AppTableWithButtons
                            title="Partner Attributions"
                            rowIdField="attributionGuid"
                            rows={permittedPartnerAttributions}
                            columns={partnerColumns}
                            pagination
                            totalRecords={permittedPartnerAttributions.length}
                            isLoading={attrsLoading}
                            onPageChange={handlePageChange}
                        />
                    )}
                </AppFormCell>
            </AppFormRow>
        </>
    );
}